import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router';
import { useAppDispatch } from 'reducers/index';

// Types
import type { UpdateCmsPageRequest } from '@zola/svc-web-api-ts-client';
import type { EditPageV2Props } from 'components/manage/website/entities/EditPageCommon/EditPageFormContainer/EditPageFormV2/index';
import type { ZolaFileResponseType } from 'actions/types/UploadcareActions';
import { UploadCareTabTypes } from '@zola-helpers/client/dist/es/uploadcare/constants';

// Zola-ui
import { ButtonV2 } from '@zola/zola-ui/src/components/Button';

// action
import { uploaded } from 'actions/website/FileUploadActions';

// React-Final-Form
import { Form, FormSpy } from 'react-final-form';
import {
  FormHiddenInput,
  FormInput,
  FormTextarea,
} from 'components/common/form/FinalFormV2Elements';
import ImageUploadV2 from 'components/common/ImageUploadV2/ImageUploadV2';
import * as Validations from '../../../../../../common/form/commonValidations';

// Components
import FileInputZolaUIButton from '../../../../../../common/form/FileInputZolaUIButton';
import FileInput from '../../../../../../common/form/FileInput';

// flags
import featureFlags from '../../../../../../../util/featureFlags';

// Content
import { getCmsPageDescriptionPlaceholder } from '../../../../CmsPageTypes';

// Styles
import '../EditPageForm.less';

const EditPageFormV2: React.FC<EditPageV2Props> = ({
  page,
  headerImgUrl,
  filePreview,
  onUpload,
  router,
  routes,
  submitPageSilently,
  submitPage,
  updatePreview,
}) => {
  const dispatch = useAppDispatch();
  // experiment
  const isUnderUploadcareExperiment = featureFlags.get('websiteToUploadcare');

  const pageType = page && page.type;
  const descriptionPlaceholder = getCmsPageDescriptionPlaceholder(pageType);
  const filePreviewUrl = filePreview && filePreview.cloudfront_url;
  const headerImagePreview = filePreviewUrl || headerImgUrl;
  const hasHeaderImage = !!headerImgUrl;
  // if true, the form hasn't been submitted with the newly uploaded image
  const [needsUpdate, setNeedsUpdate] = useState(false);

  // upload the file and tell the form it will need to submit itself
  const onUploadWithUpdate = (file: File, onFailCB: () => void) => {
    onUpload(file, onFailCB);
    setNeedsUpdate(true);
  };

  // submit the page whenever the headerImage is updated
  // AND the imageFlags are different
  // ensures header image is saved
  useEffect(() => {
    const submit = async () => {
      if (needsUpdate) {
        await submitPageSilently(page as Omit<UpdateCmsPageRequest, 'wedding_account_id'>)
          .then(() => setNeedsUpdate(false))
          .catch();
      }
    };
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasHeaderImage]);

  useEffect(() => {
    return () => {
      router.setRouteLeaveHook(routes[routes.length - 1], () => {
        // do nothing
      });
    };
  });

  function onUploadCompleted(zolaFile: ZolaFileResponseType) {
    setNeedsUpdate(true);
    dispatch(
      uploaded(
        {
          ...zolaFile,
        },
        `header_image_${pageType}`
      )
    );
  }

  return (
    <Form
      onSubmit={async (
        values: Omit<UpdateCmsPageRequest, 'wedding_account_id'> & { header_image_url?: string }
      ) => {
        await submitPage(values);
      }}
      initialValues={page}
    >
      {({ handleSubmit, form: { change, getState } }) => (
        <form onSubmit={handleSubmit} id="edit-page-form" className="edit-page-form" noValidate>
          <FormHiddenInput id="cms-page-id" name="id" />
          <FormHiddenInput id="cms-page-hidden" name="hidden" />
          <FormHiddenInput name="header_image_id" />
          <FormHiddenInput name="header_image_url" />
          <div className="title-photo-section form-group">
            <FormInput
              name="title"
              type="text"
              label="Page Title"
              id="cms-page-title"
              required
              maxChars={40}
              validate={[Validations.REQUIRED, Validations.MAX_LENGTH_40]}
              onBlurFunction={async () => {
                const { dirty, valid } = getState();
                if (dirty && valid) {
                  await handleSubmit();
                }
              }}
              className="page-title-field"
            />
            {!hasHeaderImage && !isUnderUploadcareExperiment && (
              <FileInputZolaUIButton
                types="image/*"
                inputId={`header_image_${page.type}`}
                onSelect={onUploadWithUpdate}
                btnText="Add A Photo"
                divClass="upload-image-button"
                btnClass="v2-button secondary-button"
              />
            )}
          </div>
          {isUnderUploadcareExperiment && (
            <div className="upload-image-widget">
              <label>
                Add a Photo <small>(Optional)</small>
              </label>
              <ImageUploadV2
                imageUrl={getState().values.header_image_url}
                onUploadComplete={onUploadCompleted}
                onClearImage={async () => {
                  change('header_image_id', undefined);
                  change('header_image_url', undefined);
                  await submitPageSilently(getState().values);
                }}
                effects="mirror,enhance,sharp,blur,grayscale,crop"
                tabs={[
                  UploadCareTabTypes.FILE,
                  UploadCareTabTypes.DROPBOX,
                  UploadCareTabTypes.GOOGLE_PHOTOS,
                  UploadCareTabTypes.ONE_DRIVE,
                  UploadCareTabTypes.FACEBOOK,
                  UploadCareTabTypes.INSTAGRAM,
                ]}
                analyticsLocation="WEBSITE"
                analyticsProduct="WEBSITE"
                maxSize={5}
              />
            </div>
          )}
          {hasHeaderImage && !isUnderUploadcareExperiment && (
            <div className="page-image header-image form-group">
              <div className="form-label">
                Main Image <small>(max file size 5 MB)</small>
              </div>
              <div className="edit-image-section">
                <div className="image-preview">
                  <FileInput
                    filePreview={headerImagePreview}
                    inputId={`header_image_${page.type}`}
                    onSelect={onUploadWithUpdate}
                  />
                </div>
                <ButtonV2
                  className="image-delete-button"
                  variant="tertiary"
                  onClick={async () => {
                    change('header_image_id', undefined);
                    change('header_image_url', undefined);
                    await submitPageSilently(getState().values);
                  }}
                >
                  Delete
                </ButtonV2>
              </div>
            </div>
          )}
          <FormTextarea
            name="description"
            id="cms-page-description"
            label="Description"
            placeholder={descriptionPlaceholder}
            maxChars={1000}
            rows={4}
            validate={[Validations.MAX_LENGTH_1000]}
            onBlur={async () => {
              const { dirty, valid } = getState();
              if (dirty && valid) {
                await handleSubmit();
              }
            }}
            className="page-description"
          />
          <FormSpy
            subscription={{ values: true }}
            onChange={({ values }) => {
              let { title, description, hidden, header_image_url } = values;
              title = title || '';
              description = description || '';
              hidden = hidden || false;
              header_image_url = header_image_url || '';
              updatePreview({ title, description, hidden, header_image_url });
            }}
          />
        </form>
      )}
    </Form>
  );
};

export const EditPageFormV2WithRouter = withRouter(EditPageFormV2);
